"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimpleMappingInterpreter = void 0;
const typegate_1 = require("typegate");
const setWith_1 = __importDefault(require("lodash/setWith"));
const uuid_1 = require("uuid");
function convertScopesToBlueprintScopes(scopes, blueprintOutputPath) {
    const blueprintScope = {};
    for (const scopeKey in scopes) {
        const blueprintScopeKey = blueprintOutputPath.collectionOutputPathsToCollectionNodeIdPaths[scopeKey];
        if (!blueprintScopeKey) {
            continue;
        }
        blueprintScope[blueprintScopeKey] = scopes[scopeKey];
    }
    return blueprintScope;
}
class SimpleMappingInterpreter {
    answersResolver;
    blueprint;
    blueprintOutputPaths;
    constructor(answersResolver) {
        this.answersResolver = answersResolver;
        this.blueprint = answersResolver.getBlueprint();
        this.blueprintOutputPaths = this.generateBlueprintOutputPaths();
    }
    getOutput() {
        const output = {};
        for (const key in this.blueprint.sectionGroupBlueprints) {
            const sectionGroupKey = key;
            const sectionGroup = this.blueprint.sectionGroupBlueprints[sectionGroupKey];
            const sections = this.blueprint.sectionBlueprints.filter((sectionBlueprint) => sectionBlueprint.sectionGroupKey === sectionGroupKey);
            if (sectionGroup?.repeatable) {
                const repeatableOutputPath = sectionGroup.repeatable.outputPath || sectionGroupKey;
                (0, setWith_1.default)(output, repeatableOutputPath, [], Object);
                const repetitionCount = this.answersResolver.usingNodeId().getRepetitionCount(sectionGroup.repeatable.repeatableAnswerNodeId, {}) ||
                    0;
                for (let i = 0; i < repetitionCount; i++) {
                    const sectionPath = repeatableOutputPath + `[${i}]`;
                    const surrogateIdResult = this.answersResolver
                        .usingNodeId()
                        .getInstanceId(sectionGroup.repeatable.repeatableAnswerNodeId, {
                        [sectionGroup.repeatable.repeatableAnswerNodeId]: i,
                    });
                    if (surrogateIdResult.success) {
                        (0, setWith_1.default)(output, sectionPath + '.surrogateId', surrogateIdResult.value, Object);
                    }
                    const scopes = {
                        [sectionGroup.repeatable.repeatableAnswerNodeId]: i,
                    };
                    for (const section of sections) {
                        this.getSectionOutput(section, output, sectionPath, scopes);
                    }
                }
            }
            else {
                for (const section of sections) {
                    this.getSectionOutput(section, output, '', {});
                }
            }
        }
        return (0, typegate_1.success)(output);
    }
    getSectionOutput(section, output, currentPath, scopes) {
        for (const subsection of section.subsections) {
            for (const fieldGroup of subsection.fieldGroups) {
                if (fieldGroup?.repeatable) {
                    const repeatableOutputPath = fieldGroup.repeatable.outputPath || fieldGroup.partName;
                    const fieldGroupsPath = currentPath + repeatableOutputPath;
                    (0, setWith_1.default)(output, fieldGroupsPath, [], Object);
                    const repetitionCount = this.answersResolver
                        .usingNodeId()
                        .getRepetitionCount(fieldGroup.repeatable.repeatableAnswerNodeId, scopes) || 0;
                    for (let i = 0; i < repetitionCount; i++) {
                        const questionPath = fieldGroupsPath + `[${i}]`;
                        const surrogateIdResult = this.answersResolver
                            .usingNodeId()
                            .getInstanceId(fieldGroup.repeatable.repeatableAnswerNodeId, {
                            ...scopes,
                            [fieldGroup.repeatable.repeatableAnswerNodeId]: i,
                        });
                        if (surrogateIdResult.success) {
                            (0, setWith_1.default)(output, questionPath + '.surrogateId', surrogateIdResult.value, Object);
                        }
                        const questionScopes = {
                            ...scopes,
                            [fieldGroup.repeatable.repeatableAnswerNodeId]: i,
                        };
                        for (const field of fieldGroup.fields) {
                            this.getFieldOutput(field, output, questionPath, questionScopes);
                        }
                    }
                }
                else {
                    for (const field of fieldGroup.fields) {
                        this.getFieldOutput(field, output, currentPath, scopes);
                    }
                }
            }
        }
    }
    getFieldOutput(field, output, currentPath, scopes) {
        if (!field.outputPath) {
            return;
        }
        const answer = this.answersResolver.usingNodeId().getAnswer(field.answerNodeId, scopes);
        const path = currentPath + field.outputPath;
        (0, setWith_1.default)(output, path, answer || undefined, Object);
    }
    generateBlueprintOutputPaths() {
        const paths = {};
        for (const key in this.blueprint.sectionGroupBlueprints) {
            const sectionGroupKey = key;
            const sectionGroup = this.blueprint.sectionGroupBlueprints[sectionGroupKey];
            const sections = this.blueprint.sectionBlueprints.filter((sectionBlueprint) => sectionBlueprint.sectionGroupKey === sectionGroupKey);
            const sectionGroupPath = sectionGroup?.repeatable ? sectionGroup.repeatable.outputPath || sectionGroupKey : '';
            const sectionGroupScope = {};
            if (sectionGroup?.repeatable) {
                sectionGroupScope[sectionGroup.repeatable.outputPath || sectionGroupKey] =
                    sectionGroup.repeatable.repeatableAnswerNodeId;
                if (sectionGroup.repeatable) {
                    paths[sectionGroup.repeatable.outputPath || sectionGroupKey] = {
                        path: sectionGroup.repeatable.outputPath || sectionGroupKey,
                        answerNodeId: sectionGroup.repeatable.repeatableAnswerNodeId,
                        collectionOutputPathsToCollectionNodeIdPaths: sectionGroupScope,
                    };
                    const surrogateIdPath = (sectionGroup.repeatable.outputPath || sectionGroupKey) + '/surrogateId';
                    paths[surrogateIdPath] = {
                        path: surrogateIdPath,
                        answerNodeId: sectionGroup.repeatable.repeatableAnswerNodeId,
                        collectionOutputPathsToCollectionNodeIdPaths: sectionGroupScope,
                    };
                }
            }
            for (const section of sections) {
                for (const subsection of section.subsections) {
                    for (const fieldGroup of subsection.fieldGroups) {
                        const questionScope = { ...sectionGroupScope };
                        if (fieldGroup?.repeatable) {
                            questionScope[fieldGroup.repeatable.outputPath || fieldGroup.partName] =
                                fieldGroup.repeatable.repeatableAnswerNodeId;
                        }
                        const fieldGroupPath = fieldGroup?.repeatable
                            ? sectionGroupPath + '/' + (fieldGroup.repeatable.outputPath || fieldGroup.partName)
                            : sectionGroupPath;
                        if (fieldGroup.repeatable) {
                            paths[fieldGroupPath] = {
                                path: fieldGroupPath,
                                answerNodeId: fieldGroup.repeatable?.repeatableAnswerNodeId || '',
                                collectionOutputPathsToCollectionNodeIdPaths: questionScope,
                            };
                            const surrogateIdPath = fieldGroupPath + '/surrogateId';
                            paths[surrogateIdPath] = {
                                path: surrogateIdPath,
                                answerNodeId: '',
                                collectionOutputPathsToCollectionNodeIdPaths: questionScope,
                            };
                        }
                        for (const field of fieldGroup.fields) {
                            if (field.outputPath) {
                                const fieldPath = fieldGroupPath + (fieldGroupPath === '' ? '' : '/') + field.outputPath;
                                paths[fieldPath] = {
                                    path: fieldPath,
                                    answerNodeId: field.answerNodeId,
                                    collectionOutputPathsToCollectionNodeIdPaths: questionScope,
                                };
                            }
                        }
                    }
                }
            }
        }
        return paths;
    }
    populateAnswers(answers) {
        this.answersResolver.clearAnswers();
        const stack = [];
        stack.push({
            node: answers,
            path: '',
            scopes: {},
        });
        while (stack.length) {
            const frame = stack.pop();
            if (!frame) {
                continue;
            }
            const currentNode = frame.node;
            if (Array.isArray(currentNode) && !currentNode.some((value) => typeof value !== 'object')) {
                for (const [i, collectionInstance] of currentNode.entries()) {
                    const scopeSplitPath = frame.path.split('/');
                    const scopePath = scopeSplitPath[scopeSplitPath.length - 1];
                    const path = frame.path + '/';
                    const scopes = { ...frame.scopes, [scopePath]: i };
                    const blueprintOutputPath = this.blueprintOutputPaths[frame.path];
                    if (!blueprintOutputPath) {
                        continue;
                    }
                    // Add surrogateId to repeated element
                    const collectionNodeId = blueprintOutputPath.collectionOutputPathsToCollectionNodeIdPaths[scopePath];
                    const blueprintScope = convertScopesToBlueprintScopes(scopes, blueprintOutputPath);
                    this.answersResolver.usingNodeId().setInstanceId(collectionNodeId, blueprintScope, (0, uuid_1.v4)());
                    stack.push({
                        node: collectionInstance,
                        path,
                        scopes,
                    });
                }
            }
            else if (currentNode && typeof currentNode === 'object' && !Array.isArray(currentNode)) {
                for (const key in currentNode) {
                    stack.push({
                        node: currentNode[key],
                        path: frame.path + (frame.path === '' ? '' : frame.path[frame.path.length - 1] === '/' ? '' : '.') + key,
                        scopes: frame.scopes,
                    });
                }
            }
            else {
                // It's a primitive
                const path = frame.path;
                const splitPath = path.split('/');
                const lastElementInPath = splitPath[splitPath.length - 1];
                const scopes = frame.scopes;
                const blueprintOutputPath = this.blueprintOutputPaths[path];
                if (!blueprintOutputPath) {
                    // We ignore answers that do not have a matching output path in the blueprint
                    continue;
                }
                const blueprintScopes = convertScopesToBlueprintScopes(scopes, blueprintOutputPath);
                if (lastElementInPath === 'surrogateId' && typeof frame.node === 'string') {
                    const penultimateElement = splitPath[splitPath.length - 2];
                    const collectionNodeId = blueprintOutputPath.collectionOutputPathsToCollectionNodeIdPaths[penultimateElement];
                    if (collectionNodeId) {
                        this.answersResolver.usingNodeId().setInstanceId(collectionNodeId, blueprintScopes, frame.node);
                    }
                    continue;
                }
                this.answersResolver.usingNodeId().setAnswer(frame.node, blueprintOutputPath.answerNodeId, blueprintScopes);
            }
        }
        return (0, typegate_1.success)(answers);
    }
}
exports.SimpleMappingInterpreter = SimpleMappingInterpreter;
